import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import {
  BaseBoxShapeUtil,
  Box,
  EMPTY_ARRAY,
  GeoShapeGeoStyle,
  Group2d,
  HTMLContainer,
  Rectangle2d,
  SVGContainer,
  Vec,
  WeakCache,
  approximately,
  areAnglesCompatible,
  geoShapeMigrations,
  geoShapeProps,
  getColorValue,
  getFontsFromRichText,
  isEqual,
  lerp,
  resolveLineHeightPx,
  toRichText,
  useColorMode,
  useValue
} from "@tldraw/editor";
import {
  isEmptyRichText,
  renderHtmlFromRichTextForMeasurement,
  renderPlaintextFromRichText
} from "../../utils/text/richText.mjs";
import {
  LABEL_FONT_SIZES,
  LABEL_PADDING,
  STROKE_SIZES,
  TEXT_PROPS,
  getFontFamily
} from "../shared/default-shape-constants.mjs";
import { DEFAULT_FILL_COLOR_NAMES } from "../shared/defaultFills.mjs";
import { getThemeFontFaces } from "../shared/defaultFonts.mjs";
import { getFillDefForCanvas, getFillDefForExport } from "../shared/defaultStyleDefs.mjs";
import { getFlipForResize } from "../shared/flip.mjs";
import { getDisplayValues } from "../shared/getDisplayValues.mjs";
import { HyperlinkButton } from "../shared/HyperlinkButton.mjs";
import { RichTextLabel, RichTextSVG } from "../shared/RichTextLabel.mjs";
import { useIsReadyForEditing } from "../shared/useEditablePlainText.mjs";
import { useEfficientZoomThreshold } from "../shared/useEfficientZoomThreshold.mjs";
import { GeoShapeBody } from "./GeoShapeBody.mjs";
import {
  defaultGeoTypeDefinitions,
  getGeoShapePath,
  getGeoTypeDefinition
} from "./getGeoShapePath.mjs";
const GEO_SHAPE_MIN_WIDTHS = Object.freeze({
  s: 12,
  m: 14,
  l: 16,
  xl: 20
});
const GEO_SHAPE_HORIZONTAL_ALIGNS = Object.freeze({
  start: "start",
  middle: "center",
  end: "end",
  "start-legacy": "start",
  "end-legacy": "end",
  "middle-legacy": "center"
});
const GEO_SHAPE_VERTICAL_ALIGNS = Object.freeze({
  start: "start",
  middle: "middle",
  end: "end"
});
const GEO_SHAPE_EMPTY_LABEL_SIZE = Object.freeze({ w: 0, h: 0 });
const BUILTIN_GEO_TYPES = new Set(Object.keys(defaultGeoTypeDefinitions));
class GeoShapeUtil extends BaseBoxShapeUtil {
  static type = "geo";
  static props = geoShapeProps;
  static migrations = geoShapeMigrations;
  static configure(options) {
    const opts = options;
    if (opts.customGeoTypes) {
      const validEntries = [];
      for (const [key, def] of Object.entries(opts.customGeoTypes)) {
        if (BUILTIN_GEO_TYPES.has(key)) {
          if (process.env.NODE_ENV !== "production") {
            console.warn(
              `[GeoShapeUtil.configure] customGeoTypes key "${key}" collides with a built-in geo type and will be ignored. Please use a unique name.`
            );
          }
          continue;
        }
        validEntries.push([key, def]);
      }
      if (validEntries.length > 0) {
        GeoShapeGeoStyle.addValues(
          ...validEntries.map(([k]) => k)
        );
      }
      const filtered = { ...opts, customGeoTypes: Object.fromEntries(validEntries) };
      return super.configure(filtered);
    }
    return super.configure(options);
  }
  options = {
    showTextOutline: true,
    getDefaultDisplayValues(_editor, shape, theme, colorMode) {
      const { color, size, labelColor, fill, align, verticalAlign, font } = shape.props;
      const colors = theme.colors[colorMode];
      return {
        strokeColor: getColorValue(colors, color, "solid"),
        strokeRoundness: theme.strokeWidth * STROKE_SIZES[size] * 2,
        strokeWidth: theme.strokeWidth * STROKE_SIZES[size],
        fillColor: fill === "none" ? "transparent" : fill === "semi" ? colors.solid : getColorValue(colors, color, DEFAULT_FILL_COLOR_NAMES[fill]),
        patternFillFallbackColor: getColorValue(colors, color, "semi"),
        labelColor: getColorValue(colors, labelColor, "solid"),
        // todo: separate from the solid color (or create more named colors in the palette so that these could be configured separately)
        labelFontFamily: getFontFamily(theme, font),
        labelFontSize: theme.fontSize * LABEL_FONT_SIZES[size],
        labelMinWidth: GEO_SHAPE_MIN_WIDTHS[size],
        labelExtraPadding: theme.strokeWidth * STROKE_SIZES[size],
        labelLineHeight: theme.lineHeight,
        labelFontWeight: "normal",
        labelFontVariant: "normal",
        labelFontStyle: "normal",
        labelHorizontalAlign: GEO_SHAPE_HORIZONTAL_ALIGNS[align],
        labelVerticalAlign: GEO_SHAPE_VERTICAL_ALIGNS[verticalAlign],
        labelPadding: LABEL_PADDING,
        // Margin between label edge and shape edge (in unscaled units)
        labelEdgeMargin: 8,
        // Minimum size of the shape to fit a label, based on font size and padding (in unscaled units)
        minSizeWithLabel: (LABEL_PADDING + 1) * 3
      };
    },
    getCustomDisplayValues(_editor, _shape) {
      return {};
    }
  };
  canEdit(shape) {
    return true;
  }
  getDefaultProps() {
    return {
      w: 100,
      h: 100,
      geo: "rectangle",
      dash: "draw",
      growY: 0,
      url: "",
      scale: 1,
      flipX: false,
      flipY: false,
      // Text properties
      color: "black",
      labelColor: "black",
      fill: "none",
      size: "m",
      font: "draw",
      align: "middle",
      verticalAlign: "middle",
      richText: toRichText("")
    };
  }
  getGeometry(shape) {
    const { props } = shape;
    const { scale } = props;
    const dv = getDisplayValues(this, shape);
    const path = getGeoShapePath(shape, dv.strokeWidth, this.options.customGeoTypes);
    const pathGeometry = path.toGeometry();
    const scaledW = Math.max(1, props.w);
    const scaledH = Math.max(1, props.h + props.growY);
    const unscaledShapeW = scaledW / scale;
    const unscaledShapeH = scaledH / scale;
    const isEmptyLabel = isEmptyRichText(props.richText);
    const unscaledLabelSize = isEmptyLabel ? GEO_SHAPE_EMPTY_LABEL_SIZE : this.getUnscaledLabelSize(shape);
    const unscaledMinWidth = Math.min(100, unscaledShapeW / 2);
    const unscaledMinHeight = Math.min(
      resolveLineHeightPx(dv.labelFontSize, dv.labelLineHeight) + dv.labelPadding * 2,
      unscaledShapeH / 2
    );
    const unscaledLabelW = Math.min(
      unscaledShapeW,
      Math.max(
        unscaledLabelSize.w,
        Math.min(unscaledMinWidth, Math.max(1, unscaledShapeW - dv.labelEdgeMargin))
      )
    );
    const unscaledLabelH = Math.min(
      unscaledShapeH,
      Math.max(
        unscaledLabelSize.h,
        Math.min(unscaledMinHeight, Math.max(1, unscaledShapeH - dv.labelEdgeMargin))
      )
    );
    const unscaledX = dv.labelHorizontalAlign === "start" ? 0 : dv.labelHorizontalAlign === "end" ? unscaledShapeW - unscaledLabelW : (unscaledShapeW - unscaledLabelW) / 2;
    const unscaledY = dv.labelVerticalAlign === "start" ? 0 : dv.labelVerticalAlign === "end" ? unscaledShapeH - unscaledLabelH : (unscaledShapeH - unscaledLabelH) / 2;
    const labelBounds = {
      x: unscaledX * scale,
      y: unscaledY * scale,
      width: unscaledLabelW * scale,
      height: unscaledLabelH * scale
    };
    return new Group2d({
      children: [
        pathGeometry,
        new Rectangle2d({
          ...labelBounds,
          isFilled: true,
          isLabel: true,
          excludeFromShapeBounds: true,
          isEmptyLabel
        })
      ]
    });
  }
  getHandleSnapGeometry(shape) {
    const geometry = this.getGeometry(shape);
    const outline = geometry.children[0];
    const def = getGeoTypeDefinition(shape.props.geo, this.options.customGeoTypes);
    if (!def) {
      throw new Error(`Unknown geo type: ${shape.props.geo}`);
    }
    if (def.snapType === "blobby") {
      return { outline, points: [geometry.bounds.center] };
    }
    return { outline, points: [...outline.vertices, geometry.bounds.center] };
  }
  getText(shape) {
    return renderPlaintextFromRichText(this.editor, shape.props.richText);
  }
  getFontFaces(shape) {
    if (isEmptyRichText(shape.props.richText)) {
      return EMPTY_ARRAY;
    }
    const themeFaces = getThemeFontFaces(this.editor.getCurrentTheme(), shape.props.font);
    if (themeFaces) return themeFaces;
    return getFontsFromRichText(this.editor, shape.props.richText, {
      family: `tldraw_${shape.props.font}`,
      weight: "normal",
      style: "normal"
    });
  }
  component(shape) {
    const { id, type, props } = shape;
    const { editor } = this;
    const isOnlySelected = useValue(
      "isGeoOnlySelected",
      () => shape.id === editor.getOnlySelectedShapeId(),
      [editor]
    );
    const isReadyForEditing = useIsReadyForEditing(editor, shape.id);
    const isForceSolid = useEfficientZoomThreshold(0.25 / shape.props.scale);
    const colorMode = useColorMode();
    const dv = getDisplayValues(this, shape, colorMode);
    const { w, h, richText, url } = props;
    const isEmpty = isEmptyRichText(richText);
    const showHtmlContainer = isReadyForEditing || !isEmpty;
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(SVGContainer, { children: /* @__PURE__ */ jsx(
        GeoShapeBody,
        {
          shape,
          shouldScale: true,
          forceSolid: isForceSolid,
          strokeColor: dv.strokeColor,
          strokeWidth: dv.strokeWidth,
          fillColor: dv.fillColor,
          patternFillFallbackColor: dv.patternFillFallbackColor,
          customGeoTypes: this.options.customGeoTypes
        }
      ) }),
      showHtmlContainer && /* @__PURE__ */ jsx(
        HTMLContainer,
        {
          style: {
            overflow: "hidden",
            width: w,
            height: h + props.growY
          },
          children: /* @__PURE__ */ jsx(
            RichTextLabel,
            {
              shapeId: id,
              type,
              fontFamily: dv.labelFontFamily,
              fontSize: dv.labelFontSize,
              lineHeight: dv.labelLineHeight,
              padding: dv.labelPadding,
              textAlign: dv.labelHorizontalAlign,
              verticalAlign: dv.labelVerticalAlign,
              richText,
              isSelected: isOnlySelected,
              labelColor: dv.labelColor,
              wrap: true,
              showTextOutline: this.options.showTextOutline,
              style: shape.props.scale !== 1 ? {
                transform: `scale(${shape.props.scale})`,
                transformOrigin: "top left",
                width: shape.props.w / shape.props.scale,
                height: (shape.props.h + props.growY) / shape.props.scale
              } : void 0
            }
          )
        }
      ),
      url && /* @__PURE__ */ jsx(HyperlinkButton, { url })
    ] });
  }
  getIndicatorPath(shape) {
    const isForceSolid = this.editor.getEfficientZoomLevel() < 0.25 / shape.props.scale;
    const { dash, scale } = shape.props;
    const dv = getDisplayValues(this, shape);
    const path = getGeoShapePath(shape, dv.strokeWidth, this.options.customGeoTypes);
    return path.toPath2D({
      style: dash === "draw" ? "draw" : "solid",
      strokeWidth: 1,
      passes: 1,
      randomSeed: shape.id,
      offset: 0,
      roundness: dv.strokeRoundness * scale,
      forceSolid: isForceSolid
    });
  }
  toSvg(shape, ctx) {
    const dv = getDisplayValues(this, shape, ctx.colorMode);
    const { richText, fill, scale, growY, w, h } = shape.props;
    const newShape = {
      ...shape,
      props: {
        ...shape.props,
        w: w / scale,
        h: (h + growY) / scale,
        growY: 0
        // growY throws off the path calculations, so we set it to 0
      }
    };
    ctx.addExportDef(getFillDefForExport(fill));
    let textEl;
    if (!isEmptyRichText(richText)) {
      const bounds = new Box(0, 0, newShape.props.w, (h + growY) / scale);
      textEl = /* @__PURE__ */ jsx(
        RichTextSVG,
        {
          fontSize: dv.labelFontSize,
          fontFamily: dv.labelFontFamily,
          lineHeight: dv.labelLineHeight,
          textAlign: dv.labelHorizontalAlign,
          verticalAlign: dv.labelVerticalAlign,
          labelColor: dv.labelColor,
          padding: dv.labelPadding,
          showTextOutline: this.options.showTextOutline,
          bounds,
          richText
        }
      );
    }
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        GeoShapeBody,
        {
          shouldScale: false,
          shape: newShape,
          forceSolid: false,
          strokeColor: dv.strokeColor,
          strokeWidth: dv.strokeWidth,
          fillColor: dv.fillColor,
          patternFillFallbackColor: dv.patternFillFallbackColor,
          customGeoTypes: this.options.customGeoTypes
        }
      ),
      textEl
    ] });
  }
  getCanvasSvgDefs() {
    return [getFillDefForCanvas()];
  }
  onResize(shape, { handle, newPoint, scaleX, scaleY, initialShape }) {
    const unscaledInitial = this.getUnscaledGeoProps(initialShape.props);
    let unscaledW = unscaledInitial.w * scaleX;
    let unscaledH = (unscaledInitial.h + unscaledInitial.growY) * scaleY;
    let overShrinkX = 0;
    let overShrinkY = 0;
    const dv = getDisplayValues(this, shape);
    if (!isEmptyRichText(shape.props.richText)) {
      const absUnscaledW = Math.abs(unscaledW);
      const absUnscaledH = Math.abs(unscaledH);
      const cached = getBatchLabelSizeCache(this.editor)?.get(shape.id);
      let unscaledLabelSize;
      if (cached) {
        unscaledLabelSize = cached;
      } else {
        const measureW = Math.max(absUnscaledW, dv.minSizeWithLabel);
        const measureH = Math.max(absUnscaledH, dv.minSizeWithLabel);
        unscaledLabelSize = this.measureUnscaledLabelSize({
          ...shape,
          props: {
            ...shape.props,
            w: measureW * shape.props.scale,
            h: measureH * shape.props.scale
          }
        });
      }
      const constrainedW = Math.max(absUnscaledW, unscaledLabelSize.w);
      const constrainedH = Math.max(absUnscaledH, unscaledLabelSize.h);
      overShrinkX = constrainedW - absUnscaledW;
      overShrinkY = constrainedH - absUnscaledH;
      unscaledW = constrainedW * Math.sign(unscaledW || 1);
      unscaledH = constrainedH * Math.sign(unscaledH || 1);
    }
    const scaledW = unscaledW * shape.props.scale;
    const scaledH = unscaledH * shape.props.scale;
    const offset = new Vec(0, 0);
    if (scaleX < 0) {
      offset.x += scaledW;
    }
    if (handle === "left" || handle === "top_left" || handle === "bottom_left") {
      offset.x += scaleX < 0 ? overShrinkX : -overShrinkX;
    }
    if (scaleY < 0) {
      offset.y += scaledH;
    }
    if (handle === "top" || handle === "top_left" || handle === "top_right") {
      offset.y += scaleY < 0 ? overShrinkY : -overShrinkY;
    }
    const { x, y } = offset.rot(shape.rotation).add(newPoint);
    return {
      x,
      y,
      props: {
        w: Math.max(Math.abs(scaledW), 1),
        h: Math.max(Math.abs(scaledH), 1),
        growY: 0,
        ...getFlipForResize(initialShape.props, { scaleX, scaleY })
      }
    };
  }
  onBeforeCreate(shape) {
    const { props } = shape;
    if (isEmptyRichText(props.richText)) {
      return props.growY !== 0 ? { ...shape, props: { ...props, growY: 0 } } : void 0;
    }
    const unscaledShapeH = props.h / props.scale;
    const unscaledLabelH = this.getUnscaledLabelSize(shape).h;
    const unscaledGrowY = this.calculateGrowY(
      unscaledShapeH,
      unscaledLabelH,
      props.growY / props.scale
    );
    if (unscaledGrowY !== null) {
      return {
        ...shape,
        props: { ...props, growY: unscaledGrowY * props.scale }
      };
    }
    return void 0;
  }
  onBeforeUpdate(prev, next) {
    const { props: prevProps } = prev;
    const { props: nextProps } = next;
    if (isEqual(prevProps.richText, nextProps.richText) && prevProps.font === nextProps.font && prevProps.size === nextProps.size) {
      return void 0;
    }
    const wasEmpty = isEmptyRichText(prevProps.richText);
    const isEmpty = isEmptyRichText(nextProps.richText);
    if (wasEmpty && isEmpty) {
      return void 0;
    }
    if (isEmpty) {
      return nextProps.growY !== 0 ? { ...next, props: { ...nextProps, growY: 0 } } : void 0;
    }
    const unscaledPrev = this.getUnscaledGeoProps(prevProps);
    const unscaledLabelSize = this.getUnscaledLabelSize(next);
    const { scale } = nextProps;
    if (wasEmpty && !isEmpty) {
      const expanded = this.expandShapeForFirstLabel(
        next,
        unscaledPrev.w,
        unscaledPrev.h,
        unscaledLabelSize
      );
      return {
        ...next,
        props: {
          ...nextProps,
          w: expanded.w * scale,
          h: expanded.h * scale,
          growY: 0
        }
      };
    }
    const unscaledNextW = next.props.w / scale;
    const needsWidthExpand = unscaledLabelSize.w > unscaledNextW;
    const unscaledGrowY = this.calculateGrowY(
      unscaledPrev.h,
      unscaledLabelSize.h,
      unscaledPrev.growY
    );
    if (unscaledGrowY !== null || needsWidthExpand) {
      return {
        ...next,
        props: {
          ...nextProps,
          growY: (unscaledGrowY ?? unscaledPrev.growY) * scale,
          w: Math.max(unscaledNextW, unscaledLabelSize.w) * scale
        }
      };
    }
    return void 0;
  }
  onDoubleClick(shape) {
    if (this.editor.inputs.getAltKey()) {
      switch (shape.props.geo) {
        case "rectangle": {
          return {
            ...shape,
            props: {
              geo: "check-box"
            }
          };
        }
        case "check-box": {
          return {
            ...shape,
            props: {
              geo: "rectangle"
            }
          };
        }
      }
    }
    const def = getGeoTypeDefinition(shape.props.geo, this.options.customGeoTypes);
    if (def?.onDoubleClick) {
      const result = def.onDoubleClick(shape);
      if (result) {
        return { ...shape, props: { ...shape.props, ...result.props } };
      }
    }
    return;
  }
  getInterpolatedProps(startShape, endShape, t) {
    return {
      ...(t > 0.5 ? endShape.props : startShape.props),
      w: lerp(startShape.props.w, endShape.props.w, t),
      h: lerp(startShape.props.h, endShape.props.h, t),
      scale: lerp(startShape.props.scale, endShape.props.scale, t)
    };
  }
  /**
   * Get the unscaled dimensions from a geo shape's props
   */
  getUnscaledGeoProps(props) {
    const { w, h, growY, scale } = props;
    return {
      w: w / scale,
      h: h / scale,
      growY: growY / scale
    };
  }
  /**
   * Calculate the growY needed to fit a label within a shape.
   * Returns null if no change is needed, otherwise returns the new unscaled growY value.
   */
  calculateGrowY(unscaledShapeH, unscaledLabelH, unscaledCurrentGrowY) {
    if (unscaledLabelH > unscaledShapeH) {
      return unscaledLabelH - unscaledShapeH;
    }
    if (unscaledCurrentGrowY > 0) {
      return 0;
    }
    return null;
  }
  /**
   * Calculate expanded dimensions when adding a label to a shape for the first time.
   * Ensures the shape meets minimum size requirements and is square if originally small.
   */
  expandShapeForFirstLabel(shape, unscaledW, unscaledH, unscaledLabelSize) {
    let w = Math.max(unscaledW, unscaledLabelSize.w);
    let h = Math.max(unscaledH, unscaledLabelSize.h);
    const dv = getDisplayValues(this, shape);
    if (unscaledW < dv.minSizeWithLabel && unscaledH < dv.minSizeWithLabel) {
      w = Math.max(w, dv.minSizeWithLabel);
      h = Math.max(h, dv.minSizeWithLabel);
      const maxDim = Math.max(w, h);
      w = maxDim;
      h = maxDim;
    }
    return { w, h };
  }
  _labelSizesForGeoCache = new WeakCache();
  /**
   * Get the cached label size for the shape. Don't call with empty rich text.
   */
  getUnscaledLabelSize(shape) {
    const batchCached = getBatchLabelSizeCache(this.editor)?.get(shape.id);
    if (batchCached) return batchCached;
    return this._labelSizesForGeoCache.get(shape, () => {
      return this.measureUnscaledLabelSize(shape);
    });
  }
  /**
   * Expensively measure the unscaled label size for the shape. Avoid using it if we can.
   */
  measureUnscaledLabelSize(shape) {
    const dv = getDisplayValues(this, shape);
    const html = renderHtmlFromRichTextForMeasurement(this.editor, shape.props.richText);
    const textSize = this.editor.textMeasure.measureHtml(html, {
      ...TEXT_PROPS,
      fontFamily: dv.labelFontFamily,
      fontSize: dv.labelFontSize,
      lineHeight: dv.labelLineHeight,
      minWidth: dv.labelMinWidth,
      maxWidth: Math.max(
        // Guard because a DOM nodes can't be less 0
        0,
        // A 'w' width that we're setting as the min-width
        Math.ceil(dv.labelMinWidth + dv.labelExtraPadding),
        // The actual text size
        Math.ceil(shape.props.w / shape.props.scale - dv.labelPadding * 2)
      )
    });
    return {
      w: textSize.w + dv.labelPadding * 2,
      h: textSize.h + dv.labelPadding * 2
    };
  }
}
const MIN_SIZE_WITH_LABEL = (LABEL_PADDING + 1) * 3;
const MIN_WIDTHS = GEO_SHAPE_MIN_WIDTHS;
const _batchLabelSizeCaches = /* @__PURE__ */ new WeakMap();
function setBatchLabelSizeCache(editor, cache) {
  if (cache) {
    _batchLabelSizeCaches.set(editor, cache);
  } else {
    _batchLabelSizeCaches.delete(editor);
  }
}
function getBatchLabelSizeCache(editor) {
  return _batchLabelSizeCaches.get(editor);
}
function getGeoLabelMeasurementRequest(editor, shape) {
  const { richText, font, size, w } = shape.props;
  const theme = editor.getCurrentTheme();
  const minWidth = MIN_WIDTHS[size];
  const html = renderHtmlFromRichTextForMeasurement(editor, richText);
  const opts = {
    ...TEXT_PROPS,
    fontFamily: getFontFamily(theme, font),
    fontSize: theme.fontSize * LABEL_FONT_SIZES[size],
    lineHeight: theme.lineHeight,
    minWidth,
    maxWidth: Math.max(
      // Guard because a DOM node can't be less than 0
      0,
      // A 'w' width that we're setting as the min-width
      Math.ceil(minWidth + theme.strokeWidth * STROKE_SIZES[size]),
      // The actual text size
      Math.ceil(w / shape.props.scale - LABEL_PADDING * 2)
    )
  };
  return { html, opts };
}
function getGeoResizeTargetWidth(initialProps, scaleX) {
  const unscaledInitialW = initialProps.w / initialProps.scale;
  const absUnscaledW = Math.abs(unscaledInitialW * scaleX);
  return Math.max(absUnscaledW, MIN_SIZE_WITH_LABEL);
}
function batchMeasureGeoLabels(editor, shapeSnapshots, scale, selectionRotation, isAspectRatioLocked) {
  const requests = [];
  for (const [id, snapshot] of shapeSnapshots) {
    if (!editor.isShapeOfType(snapshot.shape, "geo")) continue;
    const geoShape = snapshot.shape;
    if (isEmptyRichText(geoShape.props.richText)) continue;
    if (!areAnglesCompatible(snapshot.pageRotation, selectionRotation)) continue;
    const areWidthAndHeightAlignedWithCorrectAxis = approximately(
      (snapshot.pageRotation - selectionRotation) % Math.PI,
      0
    );
    let effectiveScaleX;
    if (isAspectRatioLocked || snapshot.isAspectRatioLocked) {
      const uniformScale = Math.max(Math.abs(scale.x), Math.abs(scale.y));
      effectiveScaleX = uniformScale;
    } else {
      effectiveScaleX = Math.abs(areWidthAndHeightAlignedWithCorrectAxis ? scale.x : scale.y);
    }
    const targetW = getGeoResizeTargetWidth(geoShape.props, effectiveScaleX);
    const tempShape = {
      ...geoShape,
      props: {
        ...geoShape.props,
        w: targetW * geoShape.props.scale
      }
    };
    const { html, opts } = getGeoLabelMeasurementRequest(editor, tempShape);
    requests.push({ id, html, opts });
  }
  if (requests.length === 0) return;
  const results = editor.textMeasure.measureHtmlBatch(
    requests.map(({ html, opts }) => ({ html, opts }))
  );
  const cache = /* @__PURE__ */ new Map();
  for (let i = 0; i < requests.length; i++) {
    cache.set(requests[i].id, {
      w: results[i].w + LABEL_PADDING * 2,
      h: results[i].h + LABEL_PADDING * 2
    });
  }
  setBatchLabelSizeCache(editor, cache);
}
export {
  GeoShapeUtil,
  batchMeasureGeoLabels,
  setBatchLabelSizeCache
};
//# sourceMappingURL=GeoShapeUtil.mjs.map
